import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { route } from '@aurelia/router';
import template from './users-list.html';
import { getAllUsers, setFamilyMember } from '../services/api.service';
import type { AppUser } from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

@route({ path: 'users', title: 'Users' })
@customElement({ name: 'users-list', template })
export class UsersList {
  private readonly i18n: I18N = resolve(I18N);

  public users: AppUser[] = [];
  public errorMessage = '';
  public loadingUsers = false;
  // userId -> true while a toggle request for that user is in flight, so the checkbox
  // disables itself individually rather than locking the whole table.
  public togglingUserIds: Record<number, boolean> = {};

  public async loading(): Promise<void> {
    await this.fetchAll();
  }

  public async fetchAll(): Promise<void> {
    this.loadingUsers = true;
    this.errorMessage = '';
    try {
      this.users = await getAllUsers();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : this.i18n.tr('users.loadError');
    } finally {
      this.loadingUsers = false;
    }
  }

  public async toggleFamilyMember(user: AppUser): Promise<void> {
    if (this.togglingUserIds[user.id]) return;
    this.togglingUserIds = { ...this.togglingUserIds, [user.id]: true };
    try {
      const updated = await setFamilyMember(user.id, !user.isFamilyMember);
      this.users = this.users.map(u => u.id === updated.id ? updated : u);
    } catch (e: unknown) {
      await ConfirmDialog.alert(
        e instanceof Error ? e.message : this.i18n.tr('users.updateError'),
        this.i18n.tr('common.error'),
      );
    } finally {
      const next = { ...this.togglingUserIds };
      delete next[user.id];
      this.togglingUserIds = next;
    }
  }
}
